import React, { createContext, useState, useCallback, useRef, ReactNode } from 'react';
import i18n from '@/shared/lib/i18n';
import type { CoercionReportEntry, NullSummaryEntry } from '@/shared/types/table-upload';

// ─── Individual upload types ───────────────────────────────────────────────────

export interface UploadProgress {
    id: string;
    fileName: string;
    fileSize: number;
    tableName: string;
    projectId: string;
    progress: number;
    status: 'pending' | 'uploading' | 'processing' | 'annotating' | 'success' | 'error';
    message: string;
    logs: string[];
    startedAt: Date;
    completedAt?: Date;
    result?: {
        table_name?: string;
        row_count?: number;
        upload_id?: number;
        null_summary?: NullSummaryEntry[];
        coercion_report?: CoercionReportEntry[];
    };
    error?: string;
}

// ─── Batch upload types ────────────────────────────────────────────────────────

export interface BatchItem {
    upload_id: number;
    sheet_name: string;
    table_name: string;
    status: 'uploading_bytes' | 'pending' | 'processing' | 'success' | 'error' | 'cancelled';
    percentage: number;
    message?: string;
    error?: string;
    queue_position?: number;
    row_count?: number;
    null_summary?: NullSummaryEntry[];
    coercion_report?: CoercionReportEntry[];
}

export interface ActiveBatch {
    id: string;
    projectId: string;
    fileName: string;
    items: BatchItem[];
    startedAt: Date;
    allDone: boolean;
}

// pollFn is injected by the caller to avoid a dependency from shared/contexts → features/table-upload
export type BatchPollFn = (uploadId: number) => Promise<{
    status: 'uploading_bytes' | 'pending' | 'processing' | 'success' | 'error' | 'cancelled';
    percentage: number;
    message?: string;
    error?: string;
    queue_position?: number;
    row_count?: number;
    null_summary?: NullSummaryEntry[];
    coercion_report?: CoercionReportEntry[];
}>;

// ─── Context interface ─────────────────────────────────────────────────────────

interface UploadContextValue {
    // Individual upload state
    uploads: UploadProgress[];
    activeUpload: UploadProgress | null;

    // Individual upload actions
    startUpload: (upload: Omit<UploadProgress, 'id' | 'startedAt' | 'logs' | 'status' | 'progress' | 'message'>) => string;
    updateUpload: (id: string, updates: Partial<UploadProgress>) => void;
    addLog: (id: string, log: string) => void;
    completeUpload: (id: string, result?: UploadProgress['result']) => void;
    failUpload: (id: string, error: string) => void;
    clearUpload: (id: string) => void;
    clearAllCompleted: () => void;

    // Batch upload state
    activeBatches: ActiveBatch[];

    /**
     * Registra um batch no contexto global e inicia o polling de cada upload_id.
     * O polling sobrevive à navegação pois vive no contexto, não no componente.
     *
     * @param pollFn    Função que busca o progresso de um upload_id (injetada para evitar
     *                  dependência circular shared → features)
     * @param onComplete Callback chamado quando todos os itens do batch terminam;
     *                  normalmente `() => queryClient.invalidateQueries(...)`
     */
    startBatchTracking: (params: {
        projectId: string;
        fileName: string;
        items: Array<{ upload_id: number; sheet_name: string; table_name: string }>;
        pollFn: BatchPollFn;
        onComplete: () => void;
    }) => string;

    /** Retorna o batch ativo para um projeto, ou null se não houver. */
    getBatchForProject: (projectId: string) => ActiveBatch | null;

    /** Remove um batch da lista (usado após o usuário fechar o painel de progresso). */
    dismissBatch: (batchId: string) => void;

    /** Marca itens como 'cancelled' e para o polling deles. Sempre chamar após o cancelamento ser confirmado no backend. */
    cancelBatchItems: (batchId: string, uploadIds: number[]) => void;

    // UI State
    isMinimized: boolean;
    setIsMinimized: (minimized: boolean) => void;
}

const UploadContext = createContext<UploadContextValue | null>(null);

interface UploadProviderProps {
    children: ReactNode;
}

export function UploadProvider({ children }: UploadProviderProps) {
    // ── Individual uploads ──────────────────────────────────────────────────────
    const [uploads, setUploads] = useState<UploadProgress[]>([]);
    const [isMinimized, setIsMinimized] = useState(false);
    const idCounterRef = useRef(0);

    const startUpload = useCallback((upload: Omit<UploadProgress, 'id' | 'startedAt' | 'logs' | 'status' | 'progress' | 'message'>) => {
        const id = `upload_${Date.now()}_${++idCounterRef.current}`;
        const newUpload: UploadProgress = {
            ...upload,
            id,
            status: 'pending',
            progress: 0,
            message: i18n.t('shared:uploadContext.starting'),
            logs: [],
            startedAt: new Date(),
        };
        setUploads(prev => [...prev, newUpload]);
        return id;
    }, []);

    const updateUpload = useCallback((id: string, updates: Partial<UploadProgress>) => {
        setUploads(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));
    }, []);

    const addLog = useCallback((id: string, log: string) => {
        setUploads(prev => prev.map(u => u.id === id ? { ...u, logs: [...u.logs, log] } : u));
    }, []);

    const completeUpload = useCallback((id: string, result?: UploadProgress['result']) => {
        setUploads(prev => prev.map(u =>
            u.id === id ? {
                ...u,
                status: 'success',
                progress: 100,
                message: i18n.t('shared:uploadContext.completed'),
                completedAt: new Date(),
                result
            } : u
        ));
        setTimeout(() => {
            setUploads(prev => prev.filter(u => u.id !== id));
        }, 3000);
    }, []);

    const failUpload = useCallback((id: string, error: string) => {
        setUploads(prev => prev.map(u =>
            u.id === id ? {
                ...u,
                status: 'error',
                message: i18n.t('shared:uploadContext.error', { error }),
                error,
                completedAt: new Date()
            } : u
        ));
    }, []);

    const clearUpload = useCallback((id: string) => {
        setUploads(prev => prev.filter(u => u.id !== id));
    }, []);

    const clearAllCompleted = useCallback(() => {
        setUploads(prev => prev.filter(u => u.status !== 'success' && u.status !== 'error'));
    }, []);

    const activeUpload = uploads.find(u =>
        u.status === 'uploading' || u.status === 'processing' || u.status === 'annotating'
    ) || null;

    // ── Batch uploads ───────────────────────────────────────────────────────────
    const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);

    // polling intervals per batch: batchId → { upload_id → intervalId }
    const batchPollingRefs = useRef<Record<string, Record<number, ReturnType<typeof setInterval>>>>({});
    // completion callbacks per batch (stable refs survive renders/navigation)
    const batchCallbacks = useRef<Record<string, () => void>>({});

    const startBatchTracking = useCallback(({
        projectId,
        fileName,
        items,
        pollFn,
        onComplete,
    }: {
        projectId: string;
        fileName: string;
        items: Array<{ upload_id: number; sheet_name: string; table_name: string }>;
        pollFn: BatchPollFn;
        onComplete: () => void;
    }): string => {
        const batchId = `batch_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

        const initialItems: BatchItem[] = items.map(i => ({
            ...i,
            status: 'pending',
            percentage: 5,
        }));

        setActiveBatches(prev => [...prev, {
            id: batchId,
            projectId,
            fileName,
            items: initialItems,
            startedAt: new Date(),
            allDone: false,
        }]);

        batchCallbacks.current[batchId] = onComplete;
        batchPollingRefs.current[batchId] = {};

        items.forEach(({ upload_id }) => {
            const pollOnce = async () => {
                try {
                    const progress = await pollFn(upload_id);

                    setActiveBatches(prev => prev.map(batch => {
                        if (batch.id !== batchId) return batch;

                        const updatedItems = batch.items.map(item =>
                            item.upload_id === upload_id
                                ? {
                                    ...item,
                                    status: progress.status,
                                    percentage: progress.percentage,
                                    message: progress.message,
                                    error: progress.error ?? (progress.status === 'error' ? progress.message : undefined),
                                    queue_position: progress.queue_position,
                                    row_count: progress.row_count,
                                    null_summary: progress.null_summary,
                                    coercion_report: progress.coercion_report,
                                }
                                : item
                        );

                        const allDone = updatedItems.every(
                            i => i.status === 'success' || i.status === 'error' || i.status === 'cancelled'
                        );

                        return { ...batch, items: updatedItems, allDone };
                    }));

                    if (progress.status === 'success' || progress.status === 'error' || progress.status === 'cancelled') {
                        clearInterval(batchPollingRefs.current[batchId]?.[upload_id]);
                        delete batchPollingRefs.current[batchId]?.[upload_id];

                        // When last interval for this batch is cleared → all done
                        if (Object.keys(batchPollingRefs.current[batchId] ?? {}).length === 0) {
                            batchCallbacks.current[batchId]?.();
                            delete batchCallbacks.current[batchId];
                        }
                    }
                } catch {
                    // keep polling on transient network errors
                }
            };

            const intervalId = setInterval(pollOnce, 2000);

            batchPollingRefs.current[batchId][upload_id] = intervalId;
            void pollOnce();
        });

        return batchId;
    }, []);

    const getBatchForProject = useCallback((projectId: string): ActiveBatch | null => {
        // Retorna o batch mais recente do projeto, independente de estar concluído.
        // O painel de progresso deve ficar visível até o usuário clicar "Novo upload"
        // e chamar dismissBatch() explicitamente — não desaparecer ao completar.
        const batches = activeBatches.filter(b => b.projectId === projectId);
        return batches[batches.length - 1] ?? null;
    }, [activeBatches]);

    const dismissBatch = useCallback((batchId: string) => {
        // Clear any remaining intervals before dismissing
        Object.values(batchPollingRefs.current[batchId] ?? {}).forEach(clearInterval);
        delete batchPollingRefs.current[batchId];
        delete batchCallbacks.current[batchId];
        setActiveBatches(prev => prev.filter(b => b.id !== batchId));
    }, []);

    const cancelBatchItems = useCallback((batchId: string, uploadIds: number[]) => {
        setActiveBatches(prev => prev.map(batch => {
            if (batch.id !== batchId) return batch;

            const updatedItems = batch.items.map(item =>
                uploadIds.includes(item.upload_id) && (item.status === 'pending' || item.status === 'processing')
                    ? { ...item, status: 'cancelled' as const }
                    : item
            );

            const allDone = updatedItems.every(
                i => i.status === 'success' || i.status === 'error' || i.status === 'cancelled'
            );

            return { ...batch, items: updatedItems, allDone };
        }));

        uploadIds.forEach(uploadId => {
            clearInterval(batchPollingRefs.current[batchId]?.[uploadId]);
            delete batchPollingRefs.current[batchId]?.[uploadId];
        });

        if (Object.keys(batchPollingRefs.current[batchId] ?? {}).length === 0) {
            batchCallbacks.current[batchId]?.();
            delete batchCallbacks.current[batchId];
        }
    }, []);

    // ── Context value ───────────────────────────────────────────────────────────

    const value: UploadContextValue = {
        uploads,
        activeUpload,
        startUpload,
        updateUpload,
        addLog,
        completeUpload,
        failUpload,
        clearUpload,
        clearAllCompleted,
        activeBatches,
        startBatchTracking,
        getBatchForProject,
        dismissBatch,
        cancelBatchItems,
        isMinimized,
        setIsMinimized,
    };

    return (
        <UploadContext.Provider value={value}>
            {children}
        </UploadContext.Provider>
    );
}

export { UploadContext };
export type { UploadContextValue };
